<div class="flex flex-col bg-white dark:bg-gray-700 overflow-hidden">
  <!-- Header -->
  <div class="bg-header px-5 py-4 flex items-center justify-between">
    <h2 class="text-white text-xl font-semibold">Access Type Information</h2>
    <button (click)="onClose()" class="text-white hover:text-gray-200 focus:outline-none">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-5 w-5">
        <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
      </svg>
    </button>
  </div>

  <!-- Body -->
  <div class="px-5 py-5">

    <div class="grid grid-cols-2 gap-4">
      <!-- Left Column: Limited to a Role -->
      <div class="space-y-4">
        @for(accessType of data.accessTypes; track accessType.value) {
          @if(accessType.value === 'limited') {
          <div class="border-l-4 border-green-500 pl-4 py-2 h-full">
            <h3 class="text-gray-800 font-medium text-base mb-2 dark:text-gray-200">{{ accessType.label }}</h3>
            <div class="mb-2">
              @for(section of limitedRoleDescription; track section.title) {
              <div class="mb-3">
                <h4 class="text-gray-700 font-medium text-sm mb-1.5 dark:text-gray-300">{{ section.title }}</h4>
                <ul class="list-disc list-outside space-y-1 ml-5">
                  @for(item of section.items; track item) {
                  <li class="text-gray-600 text-sm dark:text-gray-400 pl-2" [innerHTML]="item"></li>
                  }
                </ul>
              </div>
              }
            </div>
            @if(data.limitationsExample) {
            <div class="mt-3">
              <div class="flex items-center justify-between mb-1">
                <p class="text-xs text-gray-500 dark:text-gray-400">Example configuration in Additional Settings:</p>
                <button (click)="copyToClipboard()"
                  class="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:text-blue-400 rounded border border-blue-200 dark:border-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
                  @if(copied) {
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-3 w-3">
                      <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                    </svg>
                    <span>Copied!</span>
                  } @else {
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-3 w-3">
                      <path d="M7 3.5A1.5 1.5 0 0 1 8.5 2h3.879a1.5 1.5 0 0 1 1.06.44l3.122 3.12A1.5 1.5 0 0 1 17 6.622V12.5a1.5 1.5 0 0 1-1.5 1.5h-1v-3.379a3 3 0 0 0-.879-2.121L10.5 5.379A3 3 0 0 0 8.379 4.5H7v-1Z" />
                      <path d="M4.5 6A1.5 1.5 0 0 0 3 7.5v9A1.5 1.5 0 0 0 4.5 18h7a1.5 1.5 0 0 0 1.5-1.5v-5.879a1.5 1.5 0 0 0-.44-1.06L9.44 6.439A1.5 1.5 0 0 0 8.378 6H4.5Z" />
                    </svg>
                    <span>Copy</span>
                  }
                </button>
              </div>
              <pre class="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-3 text-xs overflow-x-auto"><code class="text-gray-800 dark:text-gray-200">{{ data.limitationsExample }}</code></pre>
              <p class="text-xs text-gray-500 mt-1 dark:text-gray-400">This shows the number of times each role can use the form.</p>
            </div>
            }
          </div>
          }
        }
      </div>

      <!-- Right Column: Auto-Assignment, Standalone, Request Access -->
      <div class="space-y-4">
        @for(accessType of data.accessTypes; track accessType.value) {
          @if(accessType.value === 'auto' || accessType.value === 'standalone' || accessType.value === 'request-access') {
          <div class="border-l-4 pl-4 py-2"
            [class.border-blue-500]="accessType.value === 'auto'"
            [class.border-purple-500]="accessType.value === 'standalone'"
            [class.border-amber-500]="accessType.value === 'request-access'"
            [class.mt-20]="accessType.value === 'standalone' || accessType.value === 'request-access'">
            <div class="flex items-center gap-2 mb-2">
              <h3 class="text-gray-800 font-medium text-base dark:text-gray-200">{{ accessType.label }}</h3>
              @if(accessType.value === 'standalone') {
              <span class="px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 rounded-full">New Feature</span>
              }
            </div>
            @if(accessType.value === 'standalone') {
            <div class="mb-2">
              @for(section of standaloneDescription; track section.title) {
              <div class="mb-3">
                <h4 class="text-gray-700 font-medium text-sm mb-1.5 dark:text-gray-300">{{ section.title }}</h4>
                <ul class="list-disc list-outside space-y-1 ml-5">
                  @for(item of section.items; track item) {
                  <li class="text-gray-600 text-sm dark:text-gray-400 pl-2" [innerHTML]="item"></li>
                  }
                </ul>
              </div>
              }
            </div>
            } @else if(accessType.value === 'auto') {
            <div class="mb-2">
              @for(section of autoAssignmentDescription; track section.title) {
              <div class="mb-3">
                <h4 class="text-gray-700 font-medium text-sm mb-1.5 dark:text-gray-300">{{ section.title }}</h4>
                <ul class="list-disc list-outside space-y-1 ml-5">
                  @for(item of section.items; track item) {
                  <li class="text-gray-600 text-sm dark:text-gray-400 pl-2" [innerHTML]="item"></li>
                  }
                </ul>
              </div>
              }
            </div>
            } @else if(accessType.value === 'request-access') {
            <p class="text-gray-600 text-sm dark:text-gray-300 mb-2" [innerHTML]="requestAccessDescription"></p>
            } @else {
            <p class="text-gray-600 text-sm dark:text-gray-300 mb-2">{{ accessType.description }}</p>
            }
          </div>
          }
        }
      </div>
    </div>
  </div>

  <!-- Footer -->
  <div class="border-t border-gray-200 dark:border-gray-600 px-5 py-4 flex justify-end">
    <button class="bg-gray-900 dark:bg-gray-800 text-white rounded hover:bg-gray-800 dark:hover:bg-gray-700 px-5 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
      (click)="onClose()">
      Close
    </button>
  </div>
</div>
